import {AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {Subscription} from "rxjs";
import {CheckResultControllerService, SimpleCheck} from "../../api";
import {StateConfigService} from "../../services/state/state-config.service";
import {ActivatedRoute, Router, UrlTree} from "@angular/router";
import {MatSort, MatSortHeader} from "@angular/material/sort";
import {
  faArrowRotateRight,
  faArrowUpRightFromSquare,
  faBoxArchive,
  faChevronDown,
  faChevronRight,
  faCircleInfo,
  faClipboardList,
  faEllipsisVertical,
  faGrid2,
  faMagnifyingGlass,
  faTable,
  faXmark
} from "@fortawesome/pro-solid-svg-icons";
import {faSpinnerThird} from "@fortawesome/pro-duotone-svg-icons";

import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {
  BicBadgeComponent,
  BicButtonComponent,
  BicButtonGroupComponent,
  BicButtonGroupItemComponent,
  BicDropdownComponent,
  BicDropdownElementComponent,
  BicDropdownTriggerDirective,
  BicInputComponent,
  BicToastService
} from "@buildinginformationcloud/bic-ui-components";
import {
  CdkCell,
  CdkCellDef,
  CdkColumnDef,
  CdkHeaderCell,
  CdkHeaderCellDef,
  CdkHeaderRow,
  CdkHeaderRowDef,
  CdkRow,
  CdkRowDef,
  CdkTable
} from "@angular/cdk/table";
import {DatePipe, NgTemplateOutlet} from "@angular/common";
import {BicFilterComponent, BicFilterConfig} from "../util/design/bic-filter/bic-filter.component";
import {FormsModule} from "@angular/forms";
import {PageHeaderComponent} from "../util/design/page-header/page-header.component";
import {EmptyStateComponent} from "../util/design/empty-state/empty-state.component";
import {MyChecksService} from "../my-checks/my-checks.service";
import {
  CheckReportService,
  CheckReportStatus,
  REPORT_RULE_TYPE_LABELS,
  ReportDateGroup
} from "../../services/checks/check-report.service";
import {ReportMyCheckView, ReportRow} from "./report-overview/report-row.model";
import {ReportMyChecksBadgeComponent} from "./report-overview/report-mychecks-badge.component";
import {ReportFilesBadgeComponent} from "./report-overview/report-files-badge.component";
import {ReportCardComponent} from "./report-overview/report-card.component";

/** Die zwei Darstellungen der Berichtsübersicht. */
export type ReportsViewMode = 'table' | 'cards';

/** localStorage-Schlüssel der zuletzt gewählten Ansicht (pro Gerät, wie `checkReportViewMode`). */
const VIEW_MODE_STORAGE_KEY = 'reportsViewMode';

function readStoredViewMode(): ReportsViewMode {
  return localStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'cards' ? 'cards' : 'table';
}

function storeViewMode(mode: ReportsViewMode): void {
  localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
}

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FaIconComponent,
    CdkTable,
    CdkHeaderRow,
    CdkRow,
    CdkRowDef,
    CdkHeaderRowDef,
    CdkCell,
    CdkHeaderCell,
    CdkColumnDef,
    CdkHeaderCellDef,
    CdkCellDef,
    DatePipe,
    MatSort,
    MatSortHeader,
    BicFilterComponent,
    BicInputComponent,
    FormsModule,
    PageHeaderComponent,
    EmptyStateComponent,
    BicDropdownComponent,
    BicDropdownElementComponent,
    BicDropdownTriggerDirective,
    BicBadgeComponent,
    BicButtonComponent,
    BicButtonGroupComponent,
    BicButtonGroupItemComponent,
    NgTemplateOutlet,
    ReportMyChecksBadgeComponent,
    ReportFilesBadgeComponent,
    ReportCardComponent,
  ]
})
export class ReportsComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Zeilen pro Render-Schritt: initial so viele, danach je Klick auf "Mehr laden" so viele dazu. */
  private static readonly PAGE_SIZE = 20;

  private checkResultService = inject(CheckResultControllerService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private stateConfigService = inject(StateConfigService);
  private checkReportService = inject(CheckReportService);
  displayedColumns: string[] = ['check', 'myChecks', 'files', 'date', 'checker', 'state', 'actions'];
  /** Gewählte Ansicht; bleibt pro Gerät erhalten. */
  viewMode: ReportsViewMode = readStoredViewMode();
  actionInProgress: string | null = null;
  loading = true;
  allChecks: SimpleCheck[] = [];
  /** Vollstaendiges Filter-/Suchergebnis - Grundlage fuer "Mehr laden" und die Restanzahl. */
  filteredRows: ReportRow[] = [];
  /** Der tatsaechlich gerenderte Ausschnitt von {@link filteredRows}. */
  visibleRows: ReportRow[] = [];
  /** {@link visibleRows} nach Datum gruppiert – Grundlage der Kartenansicht. */
  visibleGroups: ReportDateGroup<ReportRow>[] = [];
  visibleCount = ReportsComponent.PAGE_SIZE;
  isLoadingMore = false;
  /** Reihenfolge laut Figma „Filter Panel“: Status · Prüftyp · Datum · Ersteller*in. */
  filterConfig: BicFilterConfig[] = [
    {
      key: 'reportStatus',
      displayName: 'Status',
      type: 'text',
      options: ['Abgeschlossen', 'Prüfung läuft', 'Fehlgeschlagen', 'Archiviert'],
    },
    {
      key: 'ruleType',
      displayName: 'Prüftyp',
      type: 'text',
      options: REPORT_RULE_TYPE_LABELS,
    },
    {
      key: 'date',
      displayName: 'Datum',
      type: 'date',
      getOptions: () => ['Heute', 'Gestern', 'Letzte 7 Tage', 'Letzte 30 Tage', 'Letztes Jahr'],
    },
    {
      key: 'checkerName',
      displayName: 'Ersteller*in',
      type: 'text',
      getOptions: () => Array.from(new Set(this.allChecks.map(c => c.checkerName).filter(Boolean))),
    },
  ];
  checksLoaded: boolean = false;
  @ViewChild(MatSort) sort!: MatSort;
  protected readonly faSpinnerThird = faSpinnerThird;
  timer: number = 0
  protected readonly faClipboardList = faClipboardList;
  searchText: string = '';
  activeFilters: { [key: string]: any[] } = {};
  protected readonly faEllipsisVertical = faEllipsisVertical;
  protected readonly faArrowUpRightFromSquare = faArrowUpRightFromSquare;
  protected readonly faArrowRotateRight = faArrowRotateRight;
  protected readonly faMagnifyingGlass = faMagnifyingGlass;

  get isEmptyState(): boolean {
    return this.checksLoaded && !this.loading && this.allChecks.length === 0;
  }

  onFiltersChange(event: { [key: string]: any[] }) {
    this.activeFilters = event;
    this.applyFiltersAndSearch(true);
  }

  /** Handler fuer Suchfeld UND Spalten-Sortierung - beide beginnen wieder beim ersten Fenster. */
  search() {
    this.applyFiltersAndSearch(true);
  }

  get hasMore(): boolean {
    return this.filteredRows.length > this.visibleCount;
  }

  get remainingCount(): number {
    return Math.max(0, this.filteredRows.length - this.visibleCount);
  }

  /**
   * Blendet die naechsten {@link ReportsComponent.PAGE_SIZE} Zeilen ein.
   *
   * Die Daten liegen bereits vollstaendig vor – die eigentliche Arbeit ist der Render-Durchlauf
   * der neuen Zeilen. Genau dafuer steht der Spinner; es gibt bewusst kein kuenstliches Delay.
   */
  loadMore(): void {
    if (this.isLoadingMore || !this.hasMore) return;

    this.isLoadingMore = true;
    requestAnimationFrame(() => {
      this.visibleCount += ReportsComponent.PAGE_SIZE;
      this.updateVisibleRows();
      this.isLoadingMore = false;
    });
  }

  trackByRowId = (_: number, row: ReportRow): string => row.id;

  onViewModeChange(mode: unknown): void {
    this.viewMode = mode === 'cards' ? 'cards' : 'table';
    storeViewMode(this.viewMode);
  }

  protected readonly faBoxArchive = faBoxArchive;
  private myChecksService = inject(MyChecksService);
  private toastService = inject(BicToastService);
  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  private readonly POLLING_INTERVAL_MS = 15000;
  private checkCompletedSub: Subscription | null = null;

  routeToAllInCheck() {
    const projectId = this.stateConfigService.getProjectId()!;
    if (!projectId) {
      return;
    }
    this.router.navigateByUrl('/projects/' + projectId + '/all-in-check')
  }

  updateDataSource() {
    this.applyFiltersAndSearch(true);
  }

  ngAfterViewInit(): void {
    // Die Sortierung liegt bewusst nicht mehr bei MatTableDataSource: sortiert wuerde dort das,
    // was in `data` steht – also nur das Render-Fenster. Deshalb sortiert
    // applyFiltersAndSearch() den GESAMTEN Treffersatz selbst und schneidet erst danach.
    // MatSort bleibt allein fuer die Kopfzeilen-UI zustaendig; (matSortChange) ruft search().
    //
    // Erstes Fenster nachziehen, sobald MatSort seinen Startzustand
    // (matSortActive="date" / matSortDirection="desc") gesetzt hat.
    if (this.checksLoaded) {
      this.applyFiltersAndSearch(true);
    }
  }

  /** Sortierschluessel je Spalte – frueher der sortingDataAccessor der MatTableDataSource. */
  private sortValue(item: SimpleCheck, property: string): string | number {
    switch (property) {
      case 'check':
        return this.checkReportService.getOverviewName(item).toLowerCase();
      case 'myChecks':
        return item.myChecks?.length || item.ruleSetCount || (item.checkType === 'MY_CHECK' ? 1 : 0);
      case 'files':
        return item.fileSummary?.total ?? 0;
      case 'date':
        return item.checkDate ? new Date(item.checkDate).getTime() : 0;
      case 'checker':
        return item.checkerName || '';
      case 'state':
        return this.getReportStatusOrder(item);
      default:
        return (item as Record<string, unknown>)[property] as string | number;
    }
  }

  markedOrClicked(event: any) {
    if (event != 0) {
      this.timer = event;
    }
  }

  ngOnInit(): void {

    this.activatedRoute.params.subscribe(params => {
      if (params.reportId !== undefined) {
        this.router.navigate([params.reportId], {relativeTo: this.activatedRoute});
      }
    });

    this.loadReports();
    this.startPolling();

    // Sofort-Refresh sobald ein Check per SSE (CHECK_COMPLETED) fertig gemeldet wird –
    // der 15s-Poll bleibt als Fallback bestehen.
    this.checkCompletedSub = this.stateConfigService.checkCompleted$.subscribe(() => this.refreshInBackground());
  }

  protected readonly faChevronRight = faChevronRight;
  protected readonly faChevronDown = faChevronDown;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faTable = faTable;
  protected readonly faGrid2 = faGrid2;
  protected readonly faXmark = faXmark;

  ngOnDestroy(): void {
    this.stopPolling();
    this.checkCompletedSub?.unsubscribe();
  }

  loadReports(): void {
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (!projectId) {
      this.allChecks = [];
      this.loading = false;
      this.checksLoaded = true;
      this.updateDataSource();
      return;
    }

    this.loading = true;
    this.checkResultService.getCheckResultsAsSimpleChecks(projectId, 'ALL').subscribe({
      next: (checks: SimpleCheck[]) => {
        this.allChecks = checks;
        this.checksLoaded = true;
        this.loading = false;
        this.updateDataSource();
      },
      error: () => {
        this.allChecks = [];
        this.checksLoaded = true;
        this.loading = false;
        this.updateDataSource();
      },
    });
  }

  /** Whether a check has a log view (all MY_CHECK types) */
  hasLogView(check: SimpleCheck): boolean {
    return !!this.getWorkflowInstanceId(check);
  }

  /** Open the log/instance detail view for a MY_CHECK */
  openLogView(event: MouseEvent, check: SimpleCheck): void {
    event.stopPropagation();
    const instanceId = this.getWorkflowInstanceId(check);
    if (instanceId) {
      this.openInstanceDetail(instanceId, event);
    }
  }

  archiveReport(event: MouseEvent, check: SimpleCheck): void {
    event.stopPropagation();
    this.setArchiveState(check, true);
  }

  openCheck(check: SimpleCheck, event?: MouseEvent) {
    if (event) {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-log-action="true"]')) {
        return;
      }
    }
    const targetRoute = this.resolveCheckRoute(check);
    if (!targetRoute) return;
    this.openRoute(targetRoute, event);
  }

  unarchiveReport(event: MouseEvent, check: SimpleCheck): void {
    event.stopPropagation();
    this.setArchiveState(check, false);
  }

  cancelReport(event: MouseEvent, check: SimpleCheck): void {
    event.stopPropagation();
    const instanceId = this.getWorkflowInstanceId(check);
    if (!instanceId) return;
    this.actionInProgress = check.id ?? instanceId;
    this.myChecksService.cancelCheck(instanceId).subscribe({
      next: () => {
        // Update local state instead of reloading. reportStatusLabel muss mitgeleert werden,
        // sonst zeigt getReportStatusLabel() weiter das alte Server-Label ("Prüfung läuft") an,
        // bis der nächste Poll (alle 15s) frische Daten liefert.
        check.reportStatus = 'FAILED';
        check.reportStatusLabel = undefined;
        check.failed = true;
        this.actionInProgress = null;
        this.applyFiltersAndSearch(false);
      },
      error: () => this.actionInProgress = null,
    });
  }

  canCancel(check: SimpleCheck): boolean {
    return this.hasLogView(check) && this.getReportStatus(check) === 'RUNNING';
  }

  canOpenReport(check: SimpleCheck): boolean {
    return this.checkReportService.canOpenReport(check);
  }

  getCheckName(check: SimpleCheck): string {
    return this.checkReportService.getCheckName(check);
  }

  getCheckTypeLabel(check: SimpleCheck): string {
    return this.checkReportService.getCheckTypeLabel(check);
  }

  getReportStatus(check: SimpleCheck): CheckReportStatus {
    return this.checkReportService.getStatus(check);
  }

  getReportStatusLabel(check: SimpleCheck): string {
    return this.checkReportService.getStatusLabel(check);
  }

  getStatusBadgeType(check: SimpleCheck): 'success' | 'warning' | 'error' | 'info' {
    return this.checkReportService.getStatusBadgeType(check);
  }

  private startPolling(): void {
    this.pollingInterval = setInterval(() => this.refreshInBackground(true), this.POLLING_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Hintergrund-Refresh (15s-Poll und SSE).
   *
   * @param onlyWhileRunning Nur pollen, solange ueberhaupt eine Pruefung laeuft. Ohne das baute der
   *                         Poll auch auf einer rein abgeschlossenen Uebersicht alle 15s die
   *                         komplette Tabelle neu auf. Der SSE-Kanal checkCompleted$ ruft ohne
   *                         diese Bedingung auf und faengt damit Laeufe ab, die anderswo starten.
   */
  private refreshInBackground(onlyWhileRunning = false): void {
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    if (!projectId) return;
    if (onlyWhileRunning && !this.hasRunningCheck()) return;

    this.checkResultService.getCheckResultsAsSimpleChecks(projectId, 'ALL').subscribe({
      next: (checks: SimpleCheck[]) => {
        this.allChecks = checks;
        this.applyFiltersAndSearch(false);
      },
    });
  }

  private hasRunningCheck(): boolean {
    return this.allChecks.some(check => this.getReportStatus(check) === 'RUNNING');
  }

  /**
   * Filtert, sucht, sortiert und baut daraus die Zeilen-Viewmodels; danach wird das Render-Fenster
   * neu geschnitten.
   *
   * @param resetWindow Fenster auf die erste Seite zuruecksetzen. True bei Suche/Filter/Sortierung
   *                    (die Trefferliste ist dann eine andere), false beim 15s-Poll, beim
   *                    SSE-Refresh und bei lokalen Zeilen-Mutationen – sonst klappt die Liste dem
   *                    Nutzer unter den Fingern wieder auf 20 Zeilen zusammen.
   */
  private applyFiltersAndSearch(resetWindow: boolean) {
    let filteredData = [...this.allChecks];

    // If no status filter is active, hide archived reports by default
    const statusFilter = this.activeFilters['reportStatus'] || [];
    if (statusFilter.length === 0) {
      filteredData = filteredData.filter(check => this.getReportStatus(check) !== 'ARCHIVED');
    }

    // Apply filters
    filteredData = filteredData.filter(check => {
      for (let key in this.activeFilters) {
        if (this.activeFilters[key].length > 0) {
          if (key === 'date') {
            const matchesDateFilter = this.activeFilters[key].some(range => this.isDateInRange(check.checkDate, range));
            if (!matchesDateFilter) return false;
          } else if (key === 'ruleType') {
            // Ein gemischter Lauf passt, sobald einer seiner myChecks den Prüftyp hat.
            const types: string[] = this.checkReportService.getRuleTypeLabels(check);
            if (!types.some(type => this.activeFilters[key].includes(type))) return false;
          } else {
            const value = key === 'reportStatus'
              ? this.getReportStatusLabel(check)
              : (check as Record<string, unknown>)[key];
            if (!this.activeFilters[key].includes(value)) return false;
          }
        }
      }
      return true;
    });

    // Apply search
    if (this.searchText && this.searchText.trim() !== '') {
      const searchTextLower = this.searchText.toLowerCase().trim();
      filteredData = filteredData.filter(check => {
        const checkName = this.checkReportService.getOverviewName(check).toLowerCase();
        const checkerName = check.checkerName?.toLowerCase() || '';
        const reportStatus = this.getReportStatusLabel(check).toLowerCase();
        const myCheckNames = (check.myChecks ?? []).map(myCheck => myCheck.name?.toLowerCase() ?? '');

        return (
          checkName.includes(searchTextLower) ||
          checkerName.includes(searchTextLower) ||
          reportStatus.includes(searchTextLower) ||
          myCheckNames.some(name => name.includes(searchTextLower))
        );
      });
    }

    // Sortieren VOR dem Schneiden – sonst gaelte die Reihenfolge nur innerhalb des Fensters.
    this.sortInPlace(filteredData);

    this.filteredRows = filteredData.map(check => this.toRow(check));

    if (resetWindow) {
      this.visibleCount = ReportsComponent.PAGE_SIZE;
    }
    this.updateVisibleRows();
  }

  private sortInPlace(checks: SimpleCheck[]): void {
    // MatSort durchlaeuft asc -> desc -> '' (ungesetzt). Beim dritten Klick faellt die Liste damit
    // auf ihre natuerliche Ordnung zurueck: neueste zuerst, wie beim Laden der Seite.
    const sorted = !!this.sort?.direction;
    const active = sorted ? this.sort.active : 'date';
    const direction = sorted ? this.sort.direction : 'desc';

    const factor = direction === 'asc' ? 1 : -1;
    checks.sort((a, b) => {
      const valueA = this.sortValue(a, active);
      const valueB = this.sortValue(b, active);
      if (valueA === valueB) return 0;
      return (valueA < valueB ? -1 : 1) * factor;
    });
  }

  private toRow(check: SimpleCheck): ReportRow {
    const status = this.getReportStatus(check);
    const myChecks = this.toMyCheckViews(check);
    return {
      check,
      id: check.id ?? check.workflowInstanceId ?? check.checkResultId ?? '',
      name: this.checkReportService.getOverviewName(check),
      status,
      statusLabel: this.getReportStatusLabel(check),
      badgeType: this.getStatusBadgeType(check),
      canOpen: this.canOpenReport(check),
      hasLog: this.hasLogView(check),
      canCancel: this.canCancel(check),
      myChecks,
      myCheckCount: myChecks.length > 0 ? myChecks.length : (check.ruleSetCount || null),
      files: check.fileSummary ?? null,
      ruleTypes: this.checkReportService.getRuleTypeLabels(check),
    };
  }

  /**
   * Ausgeführte myChecks eines Laufs. Beim Direktstart eines myChecks (ohne Prüflauf) liefert der
   * Lauf weder eine Namensliste noch einen `ruleSetCount` – ausgeführt wurde dann genau dieses eine
   * myCheck, dessen Name der Berichtsname ist. Ohne diesen Fallback stünde dort „0 myChecks“.
   */
  private toMyCheckViews(check: SimpleCheck): ReportMyCheckView[] {
    if (check.myChecks?.length) {
      return check.myChecks.map(myCheck => ({
        name: myCheck.name || 'Unbenannter myCheck',
        typeLabel: this.checkReportService.getRuleTypeLabel(myCheck.type),
      }));
    }
    if (check.checkType === 'MY_CHECK' && !check.ruleSetCount) {
      return [{
        name: this.checkReportService.getOverviewName(check) || 'Unbenannter myCheck',
        typeLabel: this.checkReportService.getRuleTypeLabel(check.checkRuleType),
      }];
    }
    return [];
  }

  private updateVisibleRows(): void {
    this.visibleRows = this.filteredRows.slice(0, this.visibleCount);
    this.visibleGroups = this.checkReportService.groupByDate(this.visibleRows, row => row.check.checkDate);
  }

  private openInstanceDetail(instanceId: string, event?: MouseEvent) {
    event?.preventDefault();
    const projectId = this.stateConfigService.getProjectId()!;
    // Logs always open in the same tab – never spawn a new tab (e.g. middle-click).
    this.router.navigateByUrl(this.checkReportService.createLogRoute(instanceId, projectId));
  }

  private resolveCheckRoute(check: SimpleCheck): UrlTree | null {
    // Langer Druck auf die Zeile ist Textauswahl, keine Navigation.
    if (this.timer > 500) return null;
    return this.checkReportService.resolveCheckRoute(check, this.stateConfigService.getProjectId()!);
  }

  private openRoute(route: UrlTree, event?: MouseEvent): void {
    if (event?.button === 1) {
      event.preventDefault();
      window.open(this.router.serializeUrl(route), '_blank');
      return;
    }
    this.router.navigateByUrl(route);
  }

  private setArchiveState(check: SimpleCheck, archived: boolean): void {
    const projectId = this.stateConfigService.getProjectIdOrUndefined();
    const rowId = check.id;
    if (!projectId || !rowId) return;

    this.actionInProgress = rowId;
    const request = archived
      ? this.checkResultService.archiveCheckResult(rowId, projectId)
      : this.checkResultService.unarchiveCheckResult(rowId, projectId);
    request.subscribe({
      next: () => {
        check.archived = archived;
        check.reportStatus = archived ? 'ARCHIVED' : (check.failed ? 'FAILED' : check.completed ? 'COMPLETED' : 'RUNNING');
        this.actionInProgress = null;
        this.applyFiltersAndSearch(false);
        this.toastService.success(archived ? 'Bericht archiviert' : 'Bericht wiederhergestellt');
      },
      error: () => {
        this.actionInProgress = null;
        this.toastService.error(archived ? 'Archivierung fehlgeschlagen' : 'Wiederherstellung fehlgeschlagen');
      },
    });
  }

  private getWorkflowInstanceId(check: SimpleCheck): string | undefined {
    return this.checkReportService.getWorkflowInstanceId(check);
  }

  private getReportStatusOrder(check: SimpleCheck): number {
    return this.checkReportService.getStatusOrder(check);
  }

  private isDateInRange(value: any, range: string): boolean {
    if (!value) return false;

    const date = new Date(value);
    if (isNaN(date.getTime())) return false;

    // Auf den Kalendertag normieren, damit "Heute"/"Gestern" unabhängig von der Uhrzeit greifen.
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const daysAgo = (days: number): Date => {
      const day = new Date(today);
      day.setDate(today.getDate() - days);
      return day;
    };

    switch (range) {
      case 'Heute':
        return d.getTime() === today.getTime();

      case 'Gestern':
        return d.getTime() === daysAgo(1).getTime();

      case 'Letzte 7 Tage':
        return d >= daysAgo(7);

      case 'Letzte 30 Tage':
        return d >= daysAgo(30);

      case 'Letztes Jahr':
        return d >= daysAgo(365);

      default:
        return false;
    }
  }
}
